import type { PortalAccessStatus } from '@erp/shared';
import { KeyRound, ShieldCheck, UserCheck, UserX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card } from '../../components/shadcn/index.js';
import { Button, confirmDialog, FormField, Input } from '../../components/ui/index.js';
import { useIsOwner, usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';

/**
 * Staff-side management of a customer's portal login. Reads/writes
 * /parties/:id/portal-access (gated parties.write by the API). Only shown for
 * customer-type parties.
 */
export function PortalAccessCard({ partyId, phone }: { partyId: string; phone: string | null }): JSX.Element {
  const { t, i18n } = useTranslation('parties');
  const { t: tc } = useTranslation();
  // Portal logins hang off customers only, so the narrow client-book code is
  // enough for the login itself. The app-manager grant is a different order of
  // thing — it shows a CUSTOMER the shop's cost prices and margins — so it is
  // owner-only and not a permission at all (mirrors the API's route gates).
  const canWriteAll = usePermission('parties.write');
  const canWriteCustomers = usePermission('customers.write');
  const canWrite = canWriteAll || canWriteCustomers;
  const canGrantManager = useIsOwner();

  const [status, setStatus] = useState<PortalAccessStatus | null>(null);
  const [password, setPassword] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = (): void => {
    void api()
      .get<PortalAccessStatus>(`/parties/${partyId}/portal-access`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ enabled: false, phone: null, status: null, lastLoginAt: null, isAppManager: false }));
  };
  useEffect(load, [partyId]);

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await api().put(`/parties/${partyId}/portal-access`, { password });
      setPassword('');
      setOpen(false);
      load();
      toast.success(t('portalAccess.savedToast'));
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'conflict'
        ? t('portalAccess.phoneTaken', { defaultValue: "That phone is already used by another customer's portal account." })
        : code === 'unprocessable'
          ? t('portalAccess.phoneRequired', { defaultValue: 'Add a phone number to this customer first — it is their sign-in identity.' })
          : tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Grant/revoke the app-manager area. Confirmed on the way IN only: switching
   * it on hands someone who is not a staff user the shop's cost prices and every
   * order, and that deserves a deliberate second tap. Switching it off is safe.
   */
  const setManager = async (next: boolean): Promise<void> => {
    if (next && !(await confirmDialog({ message: t('portalAccess.manager.confirm') }))) return;
    setBusy(true);
    try {
      await api().put(`/parties/${partyId}/portal-access/manager`, { isAppManager: next });
      load();
      toast.success(next ? t('portalAccess.manager.grantedToast') : t('portalAccess.manager.revokedToast'));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (): Promise<void> => {
    if (!(await confirmDialog({ message: t('portalAccess.confirmDisable') }))) return;
    setBusy(true);
    try {
      await api().delete(`/parties/${partyId}/portal-access`);
      load();
      toast.success(t('portalAccess.disabledToast'));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="lg:col-span-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-fg-subtle">{t('portalAccess.title')}</h2>
        {status &&
          (status.enabled ? (
            <Badge variant="success" className="gap-1">
              <UserCheck className="h-3.5 w-3.5" aria-hidden />
              {t('portalAccess.statusEnabled')}
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <UserX className="h-3.5 w-3.5" aria-hidden />
              {status.status === 'disabled' ? t('portalAccess.statusDisabled') : t('portalAccess.notSet')}
            </Badge>
          ))}
      </div>

      <p className="mb-3 text-sm text-fg-muted">{t('portalAccess.description')}</p>

      {status && (status.phone || phone || status.lastLoginAt) && (
        <dl className="mb-3 grid grid-cols-2 gap-2 text-sm sm:max-w-md">
          <dt className="text-fg-muted">{t('portalAccess.phone', { defaultValue: 'Sign-in phone' })}</dt>
          <dd dir="ltr">{status.phone ?? phone ?? '—'}</dd>
          <dt className="text-fg-muted">{t('portalAccess.lastLogin')}</dt>
          <dd>{status.lastLoginAt ? formatDateTime(status.lastLoginAt, { locale: i18n.language }) : t('portalAccess.never')}</dd>
        </dl>
      )}

      {/* The privilege is a property of the LOGIN, so there is nothing to grant
          until one exists. */}
      {status?.enabled && (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border bg-bg-subtle/60 p-3">
          <div className="flex-1">
            <p className="flex items-center gap-1.5 text-sm font-medium">
              <ShieldCheck className="h-4 w-4 text-brand" aria-hidden />
              {t('portalAccess.manager.label')}
              {status.isAppManager && <Badge variant="success">{t('portalAccess.manager.on')}</Badge>}
            </p>
            <p className="mt-1 text-sm text-fg-muted">{t('portalAccess.manager.help')}</p>
          </div>
          {canGrantManager && (
            <Button
              variant={status.isAppManager ? 'ghost' : 'secondary'}
              loading={busy}
              onClick={() => void setManager(!status.isAppManager)}
            >
              {status.isAppManager ? t('portalAccess.manager.revoke') : t('portalAccess.manager.grant')}
            </Button>
          )}
        </div>
      )}

      {canWrite && (
        <>
          {!open ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setOpen(true)}>
                <KeyRound className="me-1.5 h-4 w-4" aria-hidden />
                {status?.enabled ? t('portalAccess.reset') : t('portalAccess.enable')}
              </Button>
              {status?.enabled && (
                <Button variant="ghost" onClick={() => void disable()} loading={busy}>
                  {t('portalAccess.disable')}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3 sm:max-w-md">
              {/* Phone IS the login — shown read-only from the party record. */}
              <p className="text-sm text-fg-muted">
                {t('portalAccess.phoneNote', { defaultValue: 'The customer signs in with their phone number:' })}{' '}
                <span className="font-mono font-semibold text-fg" dir="ltr">{phone ?? '—'}</span>
              </p>
              {!phone && (
                <p className="rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning">
                  {t('portalAccess.phoneRequired', { defaultValue: 'Add a phone number to this customer first — it is their sign-in identity.' })}
                </p>
              )}
              <FormField id="pa-password" label={t('portalAccess.password')} required helperText={t('portalAccess.passwordHint')}>
                <Input id="pa-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
              </FormField>
              <div className="flex gap-2">
                <Button onClick={() => void save()} loading={busy} disabled={!phone || password.length < 10}>{t('portalAccess.save')}</Button>
                <Button variant="ghost" onClick={() => { setOpen(false); setPassword(''); }}>{tc('actions.cancel')}</Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
